import { useContext, useMemo, type ReactNode } from "react";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import {
  useActorToneColors,
  useEffectiveIsToneColorsEnabled,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.actor.user";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useGetGraphemePrevalence } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.prevalence";
import { MorphemeTilePresentationContext } from "#morpheme-tile-presentation-context";

function ResolvedMorphemeTilePresentationProvider({ children }: { children: ReactNode }) {
  const user = useActorUser();
  const [fadeMissingMnemonicEnabled] = useDeviceSetting("fadeMissingMnemonicEnabled");
  const [svgTileGlyphsEnabled] = useDeviceSetting("svgTileGlyphsEnabled");
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const getGraphemePrevalence = useGetGraphemePrevalence();
  const isToneColorsEnabled = useEffectiveIsToneColorsEnabled();
  const toneColors = useActorToneColors();
  const value = useMemo(
    () => ({
      fadeMissingMnemonicEnabled,
      getGraphemeGlyph,
      getGraphemePrevalence,
      isToneColorsEnabled,
      svgTileGlyphsEnabled,
      toneColors,
      user,
    }),
    [
      fadeMissingMnemonicEnabled,
      svgTileGlyphsEnabled,
      getGraphemeGlyph,
      getGraphemePrevalence,
      isToneColorsEnabled,
      toneColors,
      user,
    ],
  );
  return (
    <MorphemeTilePresentationContext value={value}>{children}</MorphemeTilePresentationContext>
  );
}

/**
 * One settings/converter subscription owner per grid. Standalone tiles install the same owner;
 * nested tile/leaf boundaries reuse it without subscribing again. No per-character cache or
 * catalogue projection is retained here: the existing pure resolvers run only for mounted leaves.
 */
export function MorphemeTilePresentationProvider({ children }: { children: ReactNode }) {
  const inherited = useContext(MorphemeTilePresentationContext);
  return inherited === null ? (
    <ResolvedMorphemeTilePresentationProvider>{children}</ResolvedMorphemeTilePresentationProvider>
  ) : (
    children
  );
}
